import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'antd';

import Auth0RolesPanel from '@totem/components/administration/auth0/roles/panel/Auth0RolesPanel';
import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import ComponentHeader from '@totem/components/ComponentHeader';
import ComplianceSelector from '@totem/components/controlSystemDetail/ComplianceSelector';
import BuildingDetailControlSystemTable from '@totem/components/controlSystemReport/BuildingDetailControlSystemTable';
import ControlSystemCreateModal from '@totem/components/controlSystemReport/ControlSystemCreateModal';
import ReportContext from '@totem/components/controlSystemReport/ControlSystemReportContext';
import NameSearch from '@totem/components/controlSystemReport/NameSearch';
import DeviceExport from '@totem/components/devices/devicesContainer/DeviceExport';
import DevicesContainer, {
  containerConfiguration as devicesContainerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import MaintenanceWindowButton from '@totem/components/devices/devicesContainer/MaintenanceWindowButton';
import DocumentsContainer from '@totem/components/documents/report/documentsContainer';
import DocumentsPanel from '@totem/components/documents/report/documentsPanel';
import BuildingChartsContainer from '@totem/components/energy/BuildingChartContainer';
import BuildingKPIs from '@totem/components/energy/BuildingKPIs';
import DateRangeInput from '@totem/components/energy/DateRangeInput';
import EnergyContainer from '@totem/components/energy/EnergyContainer';
import BuildingContext from '@totem/components/portfolio/buildings/details/container/BuildingContext';
import DeviceTable from '@totem/components/portfolio/buildings/details/DeviceTable';
import BuildingKeysContainer from '@totem/components/portfolio/buildings/details/keys/BuildingKeysContainer';
import BuildingKeysPanel from '@totem/components/portfolio/buildings/details/keys/BuildingKeysPanel';
import OptimizeContainer from '@totem/components/portfolio/buildings/details/optimize/OptimizeContainer';
import OptimizePanel from '@totem/components/portfolio/buildings/details/optimize/OptimizePanel';
import { ApiTenantSite } from '@totem/components/portfolio/buildings/details/types';
import UtilityAccountsContainer from '@totem/components/portfolio/buildings/details/utilities/UtilityAccountsContainer';
import UtilityAccountsPanel from '@totem/components/portfolio/buildings/details/utilities/UtilityAccountsPanel';
import HVACSpacesContainer from '@totem/components/portfolio/buildings/hvac/HVACSpacesContainer';
import HVACSpacesPanel from '@totem/components/portfolio/buildings/hvac/HVACSpacesPanel';
import NetworkContainer from '@totem/components/portfolio/buildings/network/networkContainer';
import NetworkGraph from '@totem/components/portfolio/buildings/network/networkGraph';
import { salesOrdersContainerConfiguration } from '@totem/components/salesOrders/list/containerConfiguration';
import SalesOrdersContainer from '@totem/components/salesOrders/list/SalesOrdersContainer';
import SalesOrdersLayout, {
  SalesOrdersExtra,
} from '@totem/components/salesOrders/list/SalesOrdersLayout';
import DeviceBackupPanel from '@totem/components/services/backup/deviceBackups/DeviceBackupPanel';
import EventsContainer, {
  containerConfiguration as eventContainerConfiguration,
} from '@totem/components/services/threats/eventsContainer/EventsContainer';
import EventsTable from '@totem/components/services/threats/eventsContainer/EventsTable';
import SurveyOverview from '@totem/components/surveyV2/building/SurveyOverview';
import ComplianceSummaryContainer from '@totem/components/surveyV2/complianceSummary/complianceSummaryContainer';
import PolicyAuditReportContainer from '@totem/components/surveyV2/policyAuditReport/policyAuditReportContainer';
import ReportTable from '@totem/components/surveyV2/policyAuditReport/ReportTable';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs, {
  IBTabItem,
} from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import PageSection from '@totem/components/ui_components/page/PageSection';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SecurityGroupCheckConstraints } from '@totem/types/user';
import { getForeignKeyValue, isNotNull, isNull } from '@totem/utilities/common';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';

import '../buildingDetail.css';

const TAB_GROUP_ORDER = [
  'Reference',
  'Contracted Services',
  'Assessments',
  'Building Operations',
];

const BuildingTabs = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data, isLoading } = useContext(BuildingContext);
  const [tenant, setTenant] = useState<ApiTenantSite | null>(null);
  const [deviceTotal, setDeviceTotal] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const { totalRecords } = useContext(ReportContext);

  const securityCheckConstraints: SecurityGroupCheckConstraints = {
    regionId:
      isNotNull(data) && isNotNull(data.region) ? data.region.id : emptyId,
    buildingId: isNotNull(data) ? data.building.id : emptyId,
    controlSystemId: emptyId,
  };

  const sapientBuildingId =
    typeof data !== 'undefined' &&
    data !== null &&
    typeof data.building.keys !== 'undefined' &&
    data.building.keys !== null
      ? getForeignKeyValue(data.building.keys, 'Sapient', 'BuildingID')
      : '';

  useEffect(() => {
    if (isNull(data) || isNull(data.building)) {
      return;
    }

    fetch(`${BUILDINGS_ENDPOINT}/${data.building.id}/tenant/Auvik`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: ApiTenantSite) => {
        setTenant(result);
      });
  }, [data]);

  const getTabItems = () => {
    const tabItems: IBTabItem[] = [
      {
        key: '1',
        label: 'Compliance',
        group: 'Assessments',
        children: (
          <ComplianceSummaryContainer buildingId={[data.building.id]}>
            <ComplianceSelector />
          </ComplianceSummaryContainer>
        ),
      },
      {
        key: '2',
        label: 'Control Systems',
        group: 'Reference',
        children: (
          <IBCard
            isCovered
            title={'Control System Management'}
            extra={
              <>
                <Button type="primary" onClick={() => setOpen(true)}>
                  Add Control System
                </Button>
              </>
            }
          >
            <div styleName="search-container">
              <NameSearch />
              <div styleName="table-count">
                <RecordCountBadge count={totalRecords} />
              </div>
            </div>
            <div styleName="table-container">
              <BuildingDetailControlSystemTable />
            </div>
            {open && (
              <ControlSystemCreateModal
                open={open}
                onClose={() => setOpen(false)}
              />
            )}
          </IBCard>
        ),
      },
      {
        key: 'devices',
        label: 'Devices',
        group: 'Reference',
        children: (
          <InputContainer
            filterConfig={devicesContainerConfiguration}
            defaultSort={{
              field: 'device.displayName',
              direction: 'ascend',
            }}
            staticFilters={[{ field: 'building', values: [data.building.id] }]}
          >
            <DevicesContainer
              onRecordTotalChanged={(total) => setDeviceTotal(total)}
            >
              <IBCard
                isCovered
                title={'Devices'}
                extra={
                  <>
                    <RecordCountBadge count={deviceTotal} />
                    {permissions.maintenanceWindowEditor.hasPermissions(
                      userProfile,
                      securityCheckConstraints,
                    ) && (
                      <>
                        &nbsp;&nbsp;&nbsp;
                        <IBTooltip
                          content={permissions.maintenanceWindowEditor.tooltip}
                        >
                          <MaintenanceWindowButton />
                        </IBTooltip>
                      </>
                    )}
                    <>
                      &nbsp;&nbsp;&nbsp;
                      <DeviceExport />
                    </>
                  </>
                }
              >
                <DeviceTable
                  showMultiselect={permissions.maintenanceWindowEditor.hasPermissions(
                    userProfile,
                    securityCheckConstraints,
                  )}
                />
              </IBCard>
            </DevicesContainer>
          </InputContainer>
        ),
      },
    ];

    if (
      permissions.assessments.hasPermissions(
        userProfile,
        securityCheckConstraints,
      )
    ) {
      tabItems.push({
        key: '3',
        label: (
          <IBTooltip content={permissions.assessments.tooltip}>
            Surveys
          </IBTooltip>
        ),
        group: 'Assessments',
        children: (
          <IBCard isCovered>
            <SurveyOverview buildingId={data.building.id} />
          </IBCard>
        ),
      });
    }

    if (
      (permissions.systemAdministrator.hasPermissions(userProfile),
      securityCheckConstraints)
    ) {
      tabItems.push({
        key: 'audits',
        label: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            Audits
          </IBTooltip>
        ),
        group: 'Assessments',
        children: (
          <IBCard isCovered title={'Policy Audits'}>
            <PolicyAuditReportContainer
              staticFilters={{ buildingId: [data.building.id] }}
            >
              <ReportTable />
            </PolicyAuditReportContainer>
          </IBCard>
        ),
      });
    }

    if (
      (permissions.continuity.hasPermissions(userProfile),
      securityCheckConstraints)
    ) {
      tabItems.push({
        key: '7',
        label: (
          <IBTooltip content={permissions.continuity.tooltip}>
            System Continuity
          </IBTooltip>
        ),
        group: 'Contracted Services',
        children: (
          <DeviceBackupPanel
            pRegionId={[]}
            pBuildingId={[data.building.id]}
            pControlSystemId={[]}
            pDeviceId={[]}
            componentKey={'building.deviceBackups'}
          />
        ),
      });
    }

    if (
      isNotNull(tenant) &&
      isNotNull(data) &&
      tenant.buildingId === data.building.id
    ) {
      tabItems.push({
        key: 'network',
        label: (
          <IBTooltip content={permissions.internal.tooltip}>Network</IBTooltip>
        ),
        children: (
          <NetworkContainer buildingId={data.building.id}>
            <NetworkGraph />
          </NetworkContainer>
        ),
      });
    }

    if (
      permissions.events.hasPermissions(userProfile, securityCheckConstraints)
    ) {
      tabItems.push({
        key: '5',
        label: (
          <IBTooltip content={permissions.events.tooltip}>Events</IBTooltip>
        ),
        group: 'Contracted Services',
        children: (
          <InputContainer
            componentKey={'building.events'}
            componentLabel={'Building: Events'}
            filterConfig={eventContainerConfiguration}
            defaultSort={{
              field: 'eventTime',
              direction: 'descend',
            }}
            staticFilters={[{ field: 'building', values: [data.building.id] }]}
          >
            <EventsContainer>
              <IBCard isCovered title={'Events'}>
                <EventsTable />
              </IBCard>
            </EventsContainer>
          </InputContainer>
        ),
      });
    }

    if (
      permissions.remoteAccess.hasPermissions(
        userProfile,
        securityCheckConstraints,
      )
    ) {
      tabItems.push({
        key: '6',
        label: (
          <IBTooltip content={permissions.remoteAccess.tooltip}>
            Remote Access
          </IBTooltip>
        ),
        group: 'Contracted Services',
        children: (
          <Auth0RolesPanel
            buildingId={data.building.id}
            buildingName={data.building.name}
            componentKey={'building.remoteAccessRoles'}
          />
        ),
      });
    }

    if (sapientBuildingId !== '') {
      tabItems.push({
        key: 'sapient',
        label: 'Energy',
        children: (
          <PageSection>
            <EnergyContainer
              dateRangeType="Last 7 Days"
              buildingId={sapientBuildingId}
              selfRegisterApis={false}
              apis={{
                portfolioKpi: true,
                portfolioCarbonKpi: true,
                portfolioChart: true,
                portfolioEndUses: true,
                buildings: false,
              }}
            >
              <ComponentHeader
                title="Energy Overview"
                options={<DateRangeInput />}
              />
              <BuildingKPIs />
              <br />
              <br />
              <BuildingChartsContainer />
            </EnergyContainer>
          </PageSection>
        ),
      });
    }

    tabItems.push({
      key: 'documents',
      label: 'Documents',
      group: 'Reference',
      children: (
        <DocumentsContainer
          documentReference={{
            buildingId: data.building.id,
            regionId: data.region.id,
            objectReference: {
              id: data.building.id,
              name: data.building.name,
              type: 'Building',
              description: data.building.name,
              createdBy: '',
              createdAt: '',
            },
          }}
        >
          <DocumentsPanel />
        </DocumentsContainer>
      ),
    });

    if (
      permissions.systemAdministrator.hasPermissions(
        userProfile,
        securityCheckConstraints,
      )
    ) {
      tabItems.push({
        key: 'hvac',
        label: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            HVAC
          </IBTooltip>
        ),
        group: 'Building Operations',
        children: (
          <HVACSpacesContainer buildingId={data.building.id}>
            <HVACSpacesPanel />
          </HVACSpacesContainer>
        ),
      });

      tabItems.push({
        key: 'utilities',
        label: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            Utilities
          </IBTooltip>
        ),
        group: 'Building Operations',
        children: (
          <UtilityAccountsContainer buildingId={data.building.id}>
            <UtilityAccountsPanel />
          </UtilityAccountsContainer>
        ),
      });

      tabItems.push({
        key: 'optimize',
        label: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            Optimize
          </IBTooltip>
        ),
        group: 'Building Operations',
        children: (
          <OptimizeContainer buildingId={data.building.id}>
            <OptimizePanel />
          </OptimizeContainer>
        ),
      });
    }

    if (permissions.internalSystemAdministrator.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'keys',
        label: (
          <IBTooltip content={permissions.internalSystemAdministrator.tooltip}>
            Key
          </IBTooltip>
        ),
        group: 'Building Operations',
        children: (
          <BuildingKeysContainer buildingId={data.building.id}>
            <BuildingKeysPanel />
          </BuildingKeysContainer>
        ),
      });
    }

    if (permissions.internal.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'salesOrderLines',
        label: 'Sales Order Lines',
        group: 'Contracted Services',
        children: (
          <InputContainer
            filterConfig={salesOrdersContainerConfiguration}
            staticFilters={[
              {
                field: 'building.id',
                values: [data.building.id],
              },
            ]}
            defaultSort={{
              field: 'nsSalesOrder.tranId',
              direction: 'ascend',
            }}
            componentKey={`building-sales-orders_${data.building.id}`}
          >
            <SalesOrdersContainer>
              <IBCard
                isCovered
                title={'Sales Order Lines'}
                extra={<SalesOrdersExtra />}
              >
                <SalesOrdersLayout />
              </IBCard>
            </SalesOrdersContainer>
          </InputContainer>
        ),
      });
    }

    const groupRank = (item: IBTabItem) => {
      const index = TAB_GROUP_ORDER.indexOf(item.group ?? '');

      return index === -1 ? TAB_GROUP_ORDER.length : index;
    };

    return [...tabItems].sort(
      (itemA, itemB) => groupRank(itemA) - groupRank(itemB),
    );
  };

  return (
    <>{!isLoading && <IBTabs defaultActiveKey="2" items={getTabItems()} />}</>
  );
};

export default BuildingTabs;
